// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, useState } from "react";
import { css } from "styled-components";

interface SavedOrderNameModalProps {
    modalIsOpen?: boolean;
    onCancel: () => void;
    onSubmit: (savedOrderName: string) => void;
}

type Props = SavedOrderNameModalProps;

interface SavedOrderNameModalStyles {
    modal?: ModalPresentationProps;
    gridContainer?: GridContainerProps;
    orderNameTextField?: InjectableCss;
    gridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
}

const savedOrderNameModalStyles: SavedOrderNameModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    gridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        variant: "tertiary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = savedOrderNameModalStyles;
const SavedOrderNameModal: FC<Props> = ({ modalIsOpen, onSubmit, onCancel }) => {
    const [savedOrderName, setSavedOrderName] = useState("");
    const [inProgress, setInProgress] = useState(false);

    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSavedOrderName(event.target.value);
    };

    const modalCloseHandler = (e: any) => {
        setSavedOrderName("");
        setInProgress(false);
        onCancel?.();
    };

    const onSubmitHandler = (e: any) => {
        if (!savedOrderName) {
            return null;
        }

        onSubmit?.(savedOrderName);
    };

    return (
        <Modal
            headline={translate("Create Saved Order")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            {...styles.modal}
        >
            <GridContainer {...styles.gridContainer} data-test-selector="savedOrderNameModal">
                <GridItem {...styles.gridItem}>
                    <TextField
                        label={translate("Saved Order Name")}
                        placeholder={translate("Enter Saved Order Name")}
                        required={true}
                        disabled={inProgress}
                        maxLength={50}
                        onChange={onChangeHandler}
                        {...styles.orderNameTextField}
                    />
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    <Button {...styles.cancelButton} onClick={modalCloseHandler} aria-label={translate("Close Modal")}>
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.saveButton}
                        onClick={onSubmitHandler}
                        data-test-selector="SavedOrderNameModal_save"
                        disabled={inProgress}
                        aria-label={translate("Close")}
                    >
                        {translate("Save")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default SavedOrderNameModal;
